import { useLocale } from "next-intl";
import { getLocale } from "next-intl/server";

export {
  formatCurrency,
  formatCurrencyCompact,
} from "libs/i18n/src/lib/currency";
import {
  formatCurrency,
  formatCurrencyWithFallback,
} from "libs/i18n/src/lib/currency";
import { getBcp47, resolveLocale } from "libs/i18n/src/lib/locales";
import { getDiscountValueFromType } from "@/discounts-utils";
import { ISubscriptionAppliedDiscount } from "@api-clients/subscriptions";
import {
  type TDiscount,
  type TDiscountCodeType,
  DiscountCodeType,
} from "@./suf-store";

/**
 * Client-side hook: returns the BCP 47 locale tag (e.g. "en-GB").
 * Use with Intl APIs that need a locale string.
 */
export const useBcp47Locale = (): string => getBcp47(resolveLocale(useLocale()));

/**
 * Shared implementation for the currency formatting hooks.
 * Returns a function that formats a value as a currency string,
 * with fallback handling for `undefined`, `null`, and `NaN`.
 */
const useFormatCurrencyBase = (compact: boolean) => {
  const locale = resolveLocale(useLocale());
  return (value?: number | string | null) =>
    formatCurrencyWithFallback(value, locale, compact);
};

/**
 * Client-side hook: returns a currency formatting function bound to the
 * current locale. Accepts `undefined`/`null` and returns a fallback
 * (currency symbol + "--") instead of formatting zero.
 *
 * @example
 * const formatPrice = useFormatCurrency();
 * formatPrice(19)        // "£19.00"
 * formatPrice(undefined) // "£--"
 */
export const useFormatCurrency = () => useFormatCurrencyBase(false);

/**
 * Client-side hook: returns a compact currency formatting function (no
 * trailing zeros) bound to the current locale.
 *
 * @example
 * const formatPrice = useFormatCurrencyCompact();
 * formatPrice(10)        // "£10"
 * formatPrice(10.5)      // "£10.50"
 * formatPrice(undefined) // "£--"
 */
export const useFormatCurrencyCompact = () => useFormatCurrencyBase(true);

/**
 * Server-side: returns a currency formatting function bound to the
 * current locale. Await once, then call synchronously.
 *
 * @example
 * const formatPrice = await getServerFormatCurrency();
 * t("price", { formattedPrice: formatPrice(19) });
 */
export const getServerFormatCurrency = async () => {
  const locale = resolveLocale(await getLocale());
  return (value: number | string) => formatCurrency(value, locale);
};

/**
 * Formats a discount for display: "30%" for percentage, locale-aware
 * currency for fixed/absolute amounts. Returns "" for unknown types.
 */
export const formatDiscountValue = (
  discount: TDiscount | undefined,
  formatPrice: (value: number | string) => string,
): string => {
  if (!discount?.type || !discount?.value) {
    return "";
  }

  const type = discount.type.toUpperCase() as TDiscountCodeType;

  switch (type) {
    case DiscountCodeType.PERCENTAGE:
      return `${discount.value}%`;
    case DiscountCodeType.FIXED_AMOUNT:
    case DiscountCodeType.ABSOLUTE_TOTAL:
      return formatPrice(discount.value);
    default: {
      const _exhaustive: never = type;
      console.warn(`Unknown discount type: ${_exhaustive}`);
      return "";
    }
  }
};

/**
 * Translation interpolation values for applied subscription discounts.
 */
export const getInterpolatedValuesFromDiscount = (
  discount: ISubscriptionAppliedDiscount | null,
  formatPrice: (value: number | string) => string,
): Record<string, string> => {
  if (!discount) {
    return {};
  }

  const discountAmountMap: Record<ISubscriptionAppliedDiscount["type"], string> = {
    FIXED_AMOUNT: formatPrice(discount.amount),
    ABSOLUTE_TOTAL: formatPrice(discount.amount),
    PERCENTAGE: `${Math.round(+discount.amount)}%`,
  };
  return {
    discountAmount: discountAmountMap[discount.type],
    discount: getDiscountValueFromType(discount),
    discountValue: formatPrice(discount.totalPriceReducedBy),
  };
};
